import React, {forwardRef, Fragment, ReactElement, ReactNode} from 'react';
import {NavLink} from 'react-router';
import clsx from 'clsx';
import {Orientation} from '@ui/forms/orientation';
import {useCustomMenu} from './use-custom-menu';
import {Trans} from '@ui/i18n/trans';
import {IconSize} from '@ui/icons/svg-icon';
import {
  MenuConfig,
  MenuItemChildConfig,
  MenuItemConfig,
} from '@common/menus/menu-config';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {Tooltip} from '@ui/tooltip/tooltip';

type MatchDescendants = undefined | boolean | ((to: string) => boolean);

export interface CustomMenuProps {
  className?: string;
  matchDescendants?: MatchDescendants;
  iconClassName?: string;
  iconSize?: IconSize;
  itemClassName?:
    | string
    | ((props: {
        isActive: boolean;
        item: MenuItemConfig;
      }) => string | undefined);
  gap?: string;
  menu?: string | MenuConfig;
  children?: (
    menuItem: MenuItemConfig,
    menuItemProps: MenuItemProps,
  ) => ReactElement;
  orientation?: Orientation;
  onlyShowIcons?: boolean;
  unstyled?: boolean;
}

export function CustomMenu({
  className,
  iconClassName,
  itemClassName,
  gap = 'gap-30',
  menu: menuOrPosition,
  orientation = 'horizontal',
  children,
  matchDescendants,
  onlyShowIcons,
  iconSize,
  unstyled = false,
}: CustomMenuProps) {
  const menu = useCustomMenu(menuOrPosition);
  if (!menu) return null;

  return (
    <div
      className={clsx(
        'flex',
        gap,
        orientation === 'vertical' ? 'flex-col items-start' : 'items-center',
        className,
      )}
      data-menu-id={menu.id}
    >
      {menu.items.map(item => {
        const menuItemProps: MenuItemProps = {
          item,
          unstyled,
          onlyShowIcon: onlyShowIcons,
          matchDescendants,
          iconClassName,
          iconSize,
          className: props => {
            return typeof itemClassName === 'function'
              ? itemClassName({...props, item})
              : itemClassName;
          },
        };

        if (children) {
          return children(item, menuItemProps);
        }

        if (item.type === 'dropdown') {
          return <CustomDropdownMenuItem key={item.id} {...menuItemProps} />;
        }

        return <CustomMenuItem key={item.id} {...menuItemProps} />;
      })}
    </div>
  );
}

export interface MenuItemProps extends React.RefAttributes<HTMLAnchorElement> {
  item: MenuItemConfig | MenuItemChildConfig;
  iconClassName?: string;
  className?: (props: {isActive: boolean}) => string | undefined;
  matchDescendants?: MatchDescendants;
  onlyShowIcon?: boolean;
  iconSize?: IconSize;
  unstyled?: boolean;
  extraContent?: ReactNode;
  position?: string;
}

export const CustomMenuItem = forwardRef<HTMLAnchorElement, MenuItemProps>(
  (
    {
      item,
      className,
      matchDescendants,
      unstyled,
      onlyShowIcon,
      iconClassName,
      iconSize = 'sm',
      extraContent,
      position = 'relative',
      ...linkProps
    },
    ref,
  ) => {
    const label = <Trans message={item.label} />;
    const IconCmp = item.icon && createSvgIconFromTree(item.icon);
    let icon = IconCmp ? (
      <IconCmp size={iconSize} className={iconClassName} />
    ) : null;

    if (icon && onlyShowIcon && label) {
      icon = (
        <Tooltip label={label} placement="right">
          {icon}
        </Tooltip>
      );
    }

    const content = (
      <Fragment>
        {icon}
        {(!icon || !onlyShowIcon) && label}
      </Fragment>
    );

    const baseClassName =
      !unstyled && 'whitespace-nowrap flex items-center justify-start gap-10';
    const focusClassNames = !unstyled && 'outline-none focus-visible:ring-2';

    if (item.type === 'link') {
      return (
        <a
          className={clsx(
            baseClassName,
            className?.({isActive: false}),
            focusClassNames,
            position,
          )}
          href={item.action}
          target={item.target}
          data-menu-item-id={item.id}
          ref={ref}
          {...linkProps}
        >
          {extraContent}
          {content}
        </a>
      );
    }

    return (
      <NavLink
        end={
          typeof matchDescendants === 'function'
            ? matchDescendants(item.action || '')
            : matchDescendants
        }
        className={props =>
          clsx(baseClassName, className?.(props), focusClassNames, position)
        }
        to={item.action || '#'}
        target={item.target}
        data-menu-item-id={item.id}
        ref={ref}
        {...linkProps}
      >
        {extraContent}
        {content}
      </NavLink>
    );
  },
);

function CustomDropdownMenuItem({
  item,
  className,
  unstyled,
  onlyShowIcon,
  iconClassName,
  iconSize = 'sm',
  position = 'relative',
}: Omit<MenuItemProps, 'item'> & {item: MenuItemConfig}) {
  const label = <Trans message={item.label} />;
  const IconCmp = item.icon && createSvgIconFromTree(item.icon);

  let icon = IconCmp ? (
    <IconCmp size={iconSize} className={iconClassName} />
  ) : null;

  if (icon && onlyShowIcon && label) {
    icon = (
      <Tooltip label={label} placement="right">
        {icon}
      </Tooltip>
    );
  }

  const content = (
    <Fragment>
      {icon}
      {(!icon || !onlyShowIcon) && label}
      {(!icon || !onlyShowIcon) && (
        <span className="ml-4 text-xs opacity-70">▼</span>
      )}
    </Fragment>
  );

  const baseClassName =
    !unstyled && 'whitespace-nowrap flex items-center justify-start gap-10';
  const focusClassNames = !unstyled && 'outline-none focus-visible:ring-2';

  return (
    <div className={clsx('group/dropdown', position)} data-menu-item-id={item.id}>
      <button
        type="button"
        className={clsx(
          baseClassName,
          className?.({isActive: false}),
          focusClassNames,
          'relative',
        )}
      >
        {content}
      </button>

      {item.children?.length ? (
        <div className="invisible absolute left-0 top-full z-50 min-w-180 rounded border bg-background p-8 opacity-0 shadow-lg transition group-hover/dropdown:visible group-hover/dropdown:opacity-100 group-focus-within/dropdown:visible group-focus-within/dropdown:opacity-100">
          <div className="flex flex-col gap-4">
            {item.children.map((child: MenuItemChildConfig) => (
       <CustomMenuItem
                key={child.id}
                item={child}
                unstyled={false}
                iconClassName={iconClassName}
                iconSize={iconSize}
                className={() =>
                  'w-full rounded px-10 py-6 text-sm hover:bg-hover'
                }
                position="relative"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}